import { FC, useMemo } from 'react'
import markedFootnote from 'marked-footnote'
import { Marked, type MarkedOptions } from 'marked'
import { markdownConfig } from '../config'
import useHightlightCode from './hooks/useHightlightCode'
import createMarkedAlert from './extensions/useCreateMarkedAlert'
import useMentionMarkdown from './extensions/useMentionMardown'
import useHashTagMarkdown from './extensions/useHashTagMarkdown'
import useAddClassesExtension from './extensions/useAddClassesExtension'
import { MarkdownHtmlPreview } from '../MarkdownHtmlPreview'

type MarkdownProps = {
  markdown: string
  tagPath?: string
  options?: MarkedOptions
  className?: string
  mentionPath?: string
  footNotePrefix?: string
}

export const Markdown: FC<MarkdownProps> = ({
  markdown,
  className,
  options = { breaks: markdownConfig.useHardBreaks },
  tagPath = '/search?q=%23',
  mentionPath = '/@',
  footNotePrefix = 'footnote-markdown-',
}) => {
  const hashTagExtension = useHashTagMarkdown(tagPath)
  const mentionExtension = useMentionMarkdown(mentionPath)
  const classExtension = useAddClassesExtension()

  useHightlightCode(markdown)

  const customMarked = useMemo(() => {
    const marked = new Marked()

    marked.use(
      createMarkedAlert(),
      markedFootnote({
        keepLabels: true,
        refMarkers: true,
        prefixId: footNotePrefix,
      }),
      classExtension,
      {
        extensions: [hashTagExtension, mentionExtension],
      },
    )
    return marked
  }, [tagPath, mentionPath, footNotePrefix])

  const html = useMemo(
    () =>
      customMarked.parse(markdown, {
        gfm: true,
        ...options,
      }) as string,
    [markdown, options, customMarked],
  )

  return <MarkdownHtmlPreview className={className} htmlString={html} />
}
